import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

/**
 * Reusable two-step CSV import dialog.
 *
 * Props:
 *   open, onClose, onApplied (callback after a successful commit)
 *   title           — e.g. "Import Inventory CSV"
 *   description     — short helper line
 *   importPath      — backend path (no /api prefix), e.g. "/inventory/import"
 *   kind            — "inventory" | "logs" (controls preview field rendering)
 */
const CsvImportDialog = ({
  open,
  onClose,
  onApplied,
  title,
  description,
  importPath,
  kind,
}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setCommitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setLoading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await axios.post(`${API}${importPath}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!file || !preview) return;
    setCommitting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}${importPath}?commit=true`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const applied =
        kind === 'inventory'
          ? `${res.data.applied_creates || 0} created · ${res.data.applied_updates || 0} updated`
          : `${res.data.applied || 0} log${(res.data.applied || 0) === 1 ? '' : 's'} created`;
      toast.success(`Import complete · ${applied}`);
      reset();
      onApplied && onApplied();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  const hasErrors = preview && preview.errors > 0;
  const nothingToDo =
    preview &&
    preview.errors === 0 &&
    (preview.to_create || 0) === 0 &&
    (preview.to_update || 0) === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center bg-slate-50">
            <p className="text-sm text-slate-600 mb-3">
              Tip: if you're not sure what columns to use, click "Download Template" first —
              the export you just got matches the import schema exactly.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              disabled={loading}
              data-testid="csv-file-input"
              className="block mx-auto text-sm"
            />
            {loading && <p className="mt-3 text-sm text-slate-500">Parsing CSV…</p>}
          </div>
        )}

        {preview && (
          <div className="space-y-4" data-testid="csv-preview">
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2 items-center">
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                {preview.to_create || 0} to create
              </Badge>
              {kind === 'inventory' && (
                <>
                  <Badge className="bg-blue-100 text-blue-800 border border-blue-200">
                    {preview.to_update || 0} to update
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
                    {preview.no_change || 0} unchanged
                  </Badge>
                </>
              )}
              <Badge
                className={
                  hasErrors
                    ? 'bg-red-100 text-red-800 border border-red-200'
                    : 'bg-slate-100 text-slate-600 border border-slate-200'
                }
              >
                {preview.errors || 0} errors
              </Badge>
            </div>

            {/* Warnings */}
            {preview.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                {preview.warnings.map((w, i) => (
                  <div key={i}>⚠ {w}</div>
                ))}
              </div>
            )}

            {/* Errors */}
            {preview.error_rows?.length > 0 && (
              <div className="border border-red-200 rounded">
                <div className="bg-red-50 px-3 py-2 text-sm font-semibold text-red-900 border-b border-red-200">
                  Rows that will be rejected
                </div>
                <ul className="divide-y divide-red-100 text-sm">
                  {preview.error_rows.map((e, i) => (
                    <li key={i} className="px-3 py-2" data-testid={`csv-error-row-${e.row}`}>
                      <span className="font-mono text-xs text-red-700">Row {e.row}:</span>{' '}
                      <span className="text-red-800">{e.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Create preview */}
            {preview.create_preview?.length > 0 && (
              <details className="border border-emerald-200 rounded" open>
                <summary className="bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900 cursor-pointer">
                  Will be created ({preview.create_preview.length}
                  {preview.to_create > preview.create_preview.length ? '+' : ''})
                </summary>
                <ul className="divide-y divide-emerald-100 text-sm max-h-60 overflow-y-auto">
                  {preview.create_preview.map((c, i) => (
                    <li key={i} className="px-3 py-2 font-mono text-xs">
                      {kind === 'inventory'
                        ? `${c.part_number} — ${c.data?.name || ''} (qty ${c.data?.quantity ?? 0})`
                        : `${c.equipment_name} — ${(c.data?.work_description || '').slice(0, 80)}`}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {/* Update preview (inventory only) */}
            {kind === 'inventory' && preview.update_preview?.length > 0 && (
              <details className="border border-blue-200 rounded">
                <summary className="bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-900 cursor-pointer">
                  Will be updated ({preview.update_preview.length}
                  {preview.to_update > preview.update_preview.length ? '+' : ''})
                </summary>
                <ul className="divide-y divide-blue-100 text-sm max-h-60 overflow-y-auto">
                  {preview.update_preview.map((u, i) => (
                    <li key={i} className="px-3 py-2">
                      <span className="font-mono text-xs">{u.part_number}</span>{' '}
                      <span className="text-slate-600">
                        — {Object.keys(u.changes || {}).join(', ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {nothingToDo && (
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-600">
                Nothing to import — every row in your CSV already matches the database.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {preview && (
            <Button
              variant="outline"
              onClick={reset}
              disabled={committing}
              data-testid="csv-restart-btn"
            >
              Choose a different file
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} disabled={committing}>
            Cancel
          </Button>
          {preview && (
            <Button
              onClick={handleApply}
              disabled={hasErrors || nothingToDo || committing}
              data-testid="csv-apply-btn"
            >
              {committing ? 'Applying…' : `Apply Import`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CsvImportDialog;
